import { useEffect, useState, type FormEvent } from "react";
import type { HomeSection, ParentSearchResult } from "@kidtube/shared";
import { api } from "../api";
import { EmptyState } from "../components/Feedback";
import { formatDuration } from "../utils";

interface SearchScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
}

interface AddDialogState {
  mode: "video" | "channel";
  video: ParentSearchResult;
  selected: Set<string>;
}

export function SearchScreen({ showSuccess, showError }: SearchScreenProps) {
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [results, setResults] = useState<ParentSearchResult[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [dialog, setDialog] = useState<AddDialogState | null>(null);

  useEffect(() => {
    api.home().then(setSections).catch(showError);
  }, [showError]);

  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clean = query.trim();
    if (clean.length < 2) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.youtubeSearch(clean);
      setSubmittedQuery(clean);
      setResults(response.items);
      setNextCursor(response.nextCursor);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (nextCursor === null || loading) return;
    setLoading(true);
    try {
      const response = await api.youtubeSearch(submittedQuery, nextCursor);
      setResults((current) => [
        ...current,
        ...response.items.filter((item) => !current.some((existing) => existing.videoId === item.videoId)),
      ]);
      setNextCursor(response.nextCursor);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }

  function openAddDialog(video: ParentSearchResult) {
    setDialog({ mode: "video", video, selected: new Set() });
  }

  function openChannelDialog(video: ParentSearchResult) {
    setDialog({ mode: "channel", video, selected: new Set() });
  }

  function toggleSection(sectionId: string) {
    setDialog((current) => {
      if (current === null) return null;
      if (current.mode === "channel") {
        return {
          ...current,
          selected: current.selected.has(sectionId) ? new Set() : new Set([sectionId]),
        };
      }
      const selected = new Set(current.selected);
      if (selected.has(sectionId)) selected.delete(sectionId);
      else selected.add(sectionId);
      return { ...current, selected };
    });
  }

  async function addToHome() {
    if (dialog === null || dialog.selected.size === 0) return;
    setActing(dialog.mode === "channel" ? `channel:${dialog.video.channelId}` : dialog.video.videoId);
    try {
      if (dialog.mode === "channel") {
        const sectionId = [...dialog.selected][0];
        if (sectionId === undefined) return;
        const result = await api.addChannelVideos(sectionId, dialog.video.channelId);
        showSuccess(
          result.added > 0
            ? `Đã thêm ${result.added} video mới từ kênh ${dialog.video.channelName}; bỏ qua ${result.skipped} video.`
            : `Kênh ${dialog.video.channelName} không có video mới đủ điều kiện.`,
        );
      } else {
        await Promise.all([...dialog.selected].map((sectionId) => api.addVideo(sectionId, dialog.video.videoId)));
        showSuccess(`Đã thêm “${dialog.video.title}” vào ${dialog.selected.size} section.`);
      }
      setDialog(null);
      setSections(await api.home());
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  async function blockVideo(video: ParentSearchResult) {
    setActing(video.videoId);
    try {
      await api.blockVideo(video.videoId, "Blocked from Parent search");
      showSuccess("Đã chặn video trên toàn bộ KidTube.");
      setResults((current) => current.filter((item) => item.videoId !== video.videoId));
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  async function blockChannel(video: ParentSearchResult) {
    setActing(video.videoId);
    try {
      await api.blockChannel(video.channelId, video.channelName, "Blocked from Parent search");
      showSuccess(`Đã chặn kênh ${video.channelName}.`);
      setResults((current) => current.filter((item) => item.channelId !== video.channelId));
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">CONTENT DISCOVERY</p>
          <h1>Tìm kiếm YouTube</h1>
          <p>Search qua KidTube Cloud, SafeSearch strict và Content Filter. API key không nằm trên trình duyệt hoặc TV.</p>
        </div>
      </header>

      <form className="search-bar" onSubmit={search} role="search">
        <span aria-hidden="true">⌕</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Tìm hoạt hình, chương trình, nội dung học tập…"
          aria-label="Từ khóa YouTube"
          minLength={2}
          maxLength={120}
          required
        />
        <button type="submit" className="primary-button" disabled={loading || query.trim().length < 2}>
          {loading && results.length === 0 ? "Đang tìm…" : "Tìm kiếm"}
        </button>
      </form>

      <div className="filter-note">
        <span aria-hidden="true">✓</span>
        <p><strong>KidTube Filter đang bật.</strong> URL Shorts và video từ 180 giây trở xuống bị loại theo chính sách fail-closed.</p>
      </div>

      {results.length > 0 && (
        <div className="section-heading compact">
          <div><h2>Kết quả cho “{submittedQuery}”</h2><p>{results.length} video đã nhận từ YouTube.</p></div>
        </div>
      )}

      {results.length > 0 ? (
        <div className="search-results">
          {results.map((video) => {
            const allowed = video.filterStatus === "ALLOWED";
            const isBusy = acting === video.videoId || acting === `channel:${video.channelId}`;
            return (
              <article className={allowed ? "search-result-card" : "search-result-card filtered"} key={video.videoId}>
                <div className="result-thumbnail">
                  <img src={video.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" />
                  <span>{formatDuration(video.durationSeconds)}</span>
                  {!allowed && <div className="filtered-overlay">Không đưa vào Child UI</div>}
                </div>
                <div className="result-copy">
                  <div className="result-status-row">
                    <span className={allowed ? "content-status allowed" : "content-status blocked"}>
                      {allowed ? "Được phép" : video.filterStatus === "BLOCKED_SHORT" ? "Video ngắn / Shorts" : video.filterStatus === "NOT_EMBEDDABLE" ? "Không nhúng được" : "Đã bị chặn"}
                    </span>
                  </div>
                  <h3>{video.title}</h3>
                  <p>{video.channelName}</p>
                  <div className="result-actions">
                    <button type="button" className="primary-button" onClick={() => openAddDialog(video)} disabled={!allowed || isBusy}>
                      + Thêm vào Home
                    </button>
                    <button type="button" className="secondary-button" onClick={() => openChannelDialog(video)} disabled={isBusy}>
                      + Thêm nhanh kênh
                    </button>
                    <button type="button" className="secondary-button danger-text" onClick={() => void blockVideo(video)} disabled={isBusy}>
                      Chặn video
                    </button>
                    <button type="button" className="secondary-button" onClick={() => void blockChannel(video)} disabled={isBusy}>
                      Chặn kênh
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : hasSearched && !loading ? (
        <EmptyState icon="⌕" title="Không tìm thấy video phù hợp" description="Thử từ khóa khác. Nội dung không đạt KidTube Filter sẽ không được hiển thị cho trẻ." />
      ) : !hasSearched ? (
        <EmptyState icon="⌕" title="Tìm nội dung cho con" description="Video chỉ xuất hiện trên Home sau khi Parent chủ động thêm vào một section." />
      ) : null}

      {nextCursor !== null && (
        <button type="button" className="secondary-button load-more" onClick={() => void loadMore()} disabled={loading}>
          {loading ? "Đang tải…" : "Tải thêm kết quả"}
        </button>
      )}

      {dialog !== null && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setDialog(null);
        }}>
          <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="add-home-title">
            <header>
              <div>
                <p className="eyebrow">HOME CONTENT</p>
                <h2 id="add-home-title">{dialog.mode === "channel" ? "Thêm nhanh từ kênh" : "Thêm vào Home"}</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setDialog(null)} aria-label="Đóng">×</button>
            </header>
            <div className="modal-video">
              <img src={dialog.video.thumbnailUrl} alt="" />
              <div>
                <strong>{dialog.mode === "channel" ? dialog.video.channelName : dialog.video.title}</strong>
                <small>
                  {dialog.mode === "channel"
                    ? "Nhập một lần tối đa 25 video mới nhất. Video ngắn, bị chặn, không nhúng được hoặc đã có sẽ bị bỏ qua."
                    : dialog.video.channelName}
                </small>
              </div>
            </div>
            <fieldset className="section-checklist">
              <legend>{dialog.mode === "channel" ? "Chọn một section" : "Chọn một hoặc nhiều section"}</legend>
              {sections.map((section) => {
                const alreadyAdded = dialog.mode === "video" && section.videos.some((video) => video.videoId === dialog.video.videoId);
                return (
                  <label key={section.id} className={alreadyAdded ? "disabled" : ""}>
                    <input
                      type={dialog.mode === "channel" ? "radio" : "checkbox"}
                      name={dialog.mode === "channel" ? "channel-target-section" : undefined}
                      checked={dialog.selected.has(section.id)}
                      onChange={() => toggleSection(section.id)}
                      disabled={alreadyAdded}
                    />
                    <span>{section.title}<small>{alreadyAdded ? "Đã có video này" : `${section.videos.length} video`}</small></span>
                  </label>
                );
              })}
            </fieldset>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setDialog(null)}>Hủy</button>
              <button type="button" className="primary-button" onClick={() => void addToHome()} disabled={dialog.selected.size === 0 || acting !== null}>
                {acting !== null
                  ? "Đang thêm…"
                  : dialog.mode === "channel"
                    ? "Thêm tối đa 25 video"
                    : `Thêm vào ${dialog.selected.size || ""} section`}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
